// Croop practice launcher: YouTube-first entry with upload fallback.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Upload } from 'lucide-react';
import { VIDEO_FILE_LIMITS } from '../../constants';
import { parseYouTubeUrl } from '../services/youtubeUrlParser';
import { searchYouTubeVideos, YouTubeSearchResult } from '../services/youtubeSearchService';
import { Segment } from '../types';
import {
  TRENDING_CHOREOGRAPHIES,
  getYouTubeThumbnailUrl,
  buildCuratedPracticePreset,
  CuratedChoreography,
  CuratedPracticeDefaults,
} from './curatedChoreographies';

// A trimmed value that starts with a scheme (https://…) or "www." is treated
// as a URL the visitor is pasting, not a search query — this keeps the two
// entry points (paste link / search) from fighting over the same input.
const looksLikeUrl = (value: string): boolean =>
  /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || /^www\./i.test(value);

const SEARCH_DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 2;

interface Props {
  onChooseFile: () => void;
  /**
   * Called when a valid YouTube URL is submitted, or a curated card is
   * picked. `presetSegments`, when present, pre-populates the editor with
   * ready-made (fully editable, saveable) segments/combos; `presetDefaults`
   * sets the curated pick's starting mirror/crop/speed/mute/combo.
   */
  onPracticeYouTube?: (
    videoId: string,
    startTime?: number,
    presetSegments?: Segment[],
    presetDefaults?: CuratedPracticeDefaults
  ) => void;
}

export const LAUNCHER_SLOGANS = [
  'Practice any choreography, one segment at a time.',
  'Loop your choreography back to life!',
  'Load, segment, loop, merge, repeat!',
  'Your favourite choreographies ready to go!'
] as const;

const SLOGAN_ACCENTS: Record<string, string> = {
  'Practice any choreography, one segment at a time.': 'one segment at a time.',
  'Loop your choreography back to life!': 'back to life!',
  'Load, segment, loop, merge, repeat!': 'merge, repeat!',
  'Your favourite choreographies ready to go!': 'ready to go!'
};

const renderSlogan = (slogan: string) => {
  const accent = SLOGAN_ACCENTS[slogan];
  if (!accent || !slogan.endsWith(accent)) return slogan;

  return (
    <>
      {slogan.slice(0, -accent.length)}
      <span className="croop-slogan-accent">{accent}</span>
    </>
  );
};

interface CuratedGroupProps {
  label: string;
  items: CuratedChoreography[];
  onSelect: (
    videoId: string,
    startTime?: number,
    presetSegments?: Segment[],
    presetDefaults?: CuratedPracticeDefaults
  ) => void;
}

const CuratedGroup: React.FC<CuratedGroupProps> = ({ label, items, onSelect }) => (
  <div className="croop-curated-group">
    <h2 className="croop-curated-group-label">{label}</h2>
    <div className="croop-curated-grid">
      {items.map((item, index) => (
        <button
          key={`${item.videoId}-${index}`}
          type="button"
          className="croop-curated-card"
          onClick={() => {
            const preset = buildCuratedPracticePreset(item);
            onSelect(item.videoId, item.startTime, preset.segments, preset.defaults);
          }}
        >
          <img
            className="croop-curated-thumb"
            src={getYouTubeThumbnailUrl(item.videoId)}
            alt=""
            aria-hidden="true"
            loading="lazy"
          />
          <span className="croop-curated-title">{item.title}</span>
        </button>
      ))}
    </div>
  </div>
);

const LauncherVariantA: React.FC<Props> = ({ onChooseFile, onPracticeYouTube }) => {
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<YouTubeSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchRequestIdRef = useRef(0);
  const slogan = useMemo(
    () => LAUNCHER_SLOGANS[Math.floor(Math.random() * LAUNCHER_SLOGANS.length)],
    []
  );

  const isUrlEntry = looksLikeUrl(url.trim());

  useEffect(() => {
    const query = url.trim();

    if (isUrlEntry || query.length < MIN_QUERY_LENGTH) {
      setSearchResults([]);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    setSearchLoading(true);
    setSearchError(null);

    const timer = setTimeout(() => {
      searchYouTubeVideos(query)
        .then(results => {
          if (searchRequestIdRef.current !== requestId) return;
          setSearchResults(results);
          setSearchLoading(false);
          setDropdownOpen(true);
        })
        .catch(err => {
          if (searchRequestIdRef.current !== requestId) return;
          setSearchResults([]);
          setSearchLoading(false);
          setSearchError(err instanceof Error ? err.message : 'YouTube search failed.');
          setDropdownOpen(true);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, isUrlEntry]);

  const selectVideo = (videoId: string, startTime?: number) => {
    setDropdownOpen(false);
    setSearchResults([]);
    setUrl('');
    if (onPracticeYouTube) {
      onPracticeYouTube(videoId, startTime);
    }
  };

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError(null);

    if (isUrlEntry) {
      try {
        const parsed = parseYouTubeUrl(url);
        selectVideo(parsed.videoId, parsed.startTime);
      } catch (err) {
        setUrlError(err instanceof Error ? err.message : 'Invalid YouTube URL.');
      }
      return;
    }

    // Free-text query: submitting practices the top search result, same as clicking it.
    if (searchResults.length > 0) {
      selectVideo(searchResults[0].videoId);
    }
  };

  const showDropdown =
    dropdownOpen && !isUrlEntry && url.trim().length >= MIN_QUERY_LENGTH &&
    (searchLoading || searchError !== null || searchResults.length > 0);

  return (
    <div className="croop-launcher">
      <div className="croop-front-stage">
        <h1 className="croop-slogan">
          {renderSlogan(slogan)}
        </h1>

        <form onSubmit={handleUrlSubmit} className="croop-action-form">
          <div className={`croop-action-container ${urlError ? 'has-error' : ''}`}>
            <Search size={20} className="croop-action-icon" />
            <input
              className="croop-action-input"
              type="text"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (urlError) setUrlError(null);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Search YouTube or paste a link..."
              role="combobox"
              aria-expanded={showDropdown}
              aria-controls="croop-search-listbox"
              aria-autocomplete="list"
              autoComplete="off"
            />
            <button
              className="croop-primary-button"
              type="submit"
              disabled={!url.trim() || (!isUrlEntry && searchResults.length === 0)}
            >
              Practice
            </button>

            {showDropdown && (
              <div
                id="croop-search-listbox"
                className="croop-search-dropdown"
                role="listbox"
                onMouseDown={(e) => e.preventDefault()}
              >
                {searchLoading && (
                  <p className="croop-search-status">Searching YouTube...</p>
                )}
                {!searchLoading && searchError && (
                  <p className="croop-search-status croop-search-status-error">{searchError}</p>
                )}
                {!searchLoading && !searchError && searchResults.length === 0 && (
                  <p className="croop-search-status">No videos found.</p>
                )}
                {!searchLoading && searchResults.map(result => (
                  <button
                    key={result.videoId}
                    type="button"
                    role="option"
                    aria-selected="false"
                    className="croop-search-result"
                    onClick={() => selectVideo(result.videoId)}
                  >
                    <img
                      className="croop-search-result-thumb"
                      src={result.thumbnailUrl}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                    />
                    <span className="croop-search-result-meta">
                      <span className="croop-search-result-title">{result.title}</span>
                      {result.channelTitle && (
                        <span className="croop-search-result-channel">{result.channelTitle}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </form>

        {urlError && (
          <p
            role="alert"
            className="croop-form-error"
          >
            {urlError}
          </p>
        )}

        <button
          type="button"
          className="croop-upload-link"
          onClick={onChooseFile}
        >
          <Upload size={16} />
          Or upload a video file (Max {VIDEO_FILE_LIMITS.MAX_SIZE_MB}MB)
        </button>

        {onPracticeYouTube && (
          <div className="croop-curated-picks">
            {/* All-Time Favorites still placeholder data — re-enable once real picks are marked.
            <CuratedGroup
              label="All-Time Favorites"
              items={ALL_TIME_CHOREOGRAPHIES}
              onSelect={onPracticeYouTube}
            />
            */}
            <CuratedGroup
              label="Trending Now"
              items={TRENDING_CHOREOGRAPHIES}
              onSelect={onPracticeYouTube}
            />
          </div>
        )}

        <a href="/privacy" className="croop-launcher-privacy-link">Privacy Policy</a>
      </div>
    </div>
  );
};

export default LauncherVariantA;
